/**
 * FEEDBACK_WIDGET — Testing-phase bug report / feature request widget.
 *
 * TO REMOVE AFTER TESTING:
 *   1. Delete this file (src/components/FeedbackWidget.js)
 *   2. Remove the one import + one JSX line from Sidebar.js  (search "FEEDBACK_WIDGET")
 *   3. Delete src/pages/OwnerFeedback.js
 *   4. Remove the /owner/feedback route from App.js          (search "FEEDBACK_WIDGET")
 *   5. Remove the Feedback Inbox sidebar entry from Sidebar.js (search "FEEDBACK_WIDGET")
 *   6. Drop table feedback_reports; (optional DB cleanup)
 */

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

export default function FeedbackWidget() {
  const location = useLocation();
  const [open, setOpen]       = useState(false);
  const [type, setType]       = useState('bug');
  const [desc, setDesc]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [done, setDone]       = useState(false);
  const [error, setError]     = useState('');

  const reset = () => { setDesc(''); setType('bug'); setDone(false); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (!desc.trim()) { setError('Please describe the issue or request.'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ type, description: desc, page_url: location.pathname }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* ── Floating trigger button ── */}
      <button
        onClick={() => { reset(); setOpen(true); }}
        title="Report a bug or request a feature"
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 9000,
          background: '#0e1b2c', color: '#fff',
          border: '2px solid #10b981', borderRadius: '999px',
          padding: '8px 16px', fontSize: '13px', fontWeight: 700,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          fontFamily: 'Inter, sans-serif',
          transition: 'transform .15s',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
      >
        🐛 Feedback
      </button>

      {/* ── Modal ── */}
      {open && (
        <div
          onClick={e => { if (e.target === e.currentTarget) { setOpen(false); } }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 9100, display: 'flex', alignItems: 'flex-end',
            justifyContent: 'flex-end', padding: '0 24px 80px 0',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          <div style={{
            background: '#fff', borderRadius: '16px',
            width: '360px', boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ background: '#0e1b2c', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '15px' }}>Send Feedback</div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
            </div>

            <div style={{ padding: '20px 18px' }}>
              {done ? (
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>✅</div>
                  <div style={{ fontWeight: 700, fontSize: '15px', color: '#0e1b2c', marginBottom: '6px' }}>Thanks!</div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>Your {type === 'bug' ? 'bug report' : 'feature request'} has been logged.</div>
                  <button
                    onClick={() => { reset(); setOpen(false); }}
                    style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 24px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={submit}>
                  {/* Type selector */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                    {[['bug','🐛 Bug Report'],['feature','✨ Feature Request']].map(([val,label]) => (
                      <button
                        key={val} type="button"
                        onClick={() => setType(val)}
                        style={{
                          padding: '10px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                          cursor: 'pointer', transition: 'all .15s',
                          border: type === val ? '2px solid #10b981' : '2px solid #e5e7eb',
                          background: type === val ? '#f0fdf4' : '#fff',
                          color: type === val ? '#065f46' : '#6b7280',
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Page (auto-filled, read-only) */}
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '4px' }}>
                      Page
                    </label>
                    <div style={{ fontSize: '12px', color: '#6b7280', background: '#f9fafb', padding: '7px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', fontFamily: 'monospace' }}>
                      {location.pathname}
                    </div>
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '4px' }}>
                      {type === 'bug' ? 'What went wrong?' : 'What would you like?'} *
                    </label>
                    <textarea
                      required autoFocus
                      value={desc}
                      onChange={e => setDesc(e.target.value)}
                      placeholder={type === 'bug'
                        ? 'Describe what happened and what you expected…'
                        : 'Describe the feature you\'d like to see…'}
                      style={{
                        width: '100%', padding: '9px 10px', border: '1px solid #d1d5db',
                        borderRadius: '8px', fontSize: '13px', fontFamily: 'Inter, sans-serif',
                        resize: 'vertical', minHeight: '90px', boxSizing: 'border-box',
                        outline: 'none', color: '#111827',
                      }}
                      onFocus={e => e.target.style.borderColor = '#10b981'}
                      onBlur={e => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>

                  {error && (
                    <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 10px', borderRadius: '6px', fontSize: '12px', marginBottom: '12px' }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit" disabled={saving}
                    style={{
                      width: '100%', background: '#10b981', color: '#fff', border: 'none',
                      borderRadius: '8px', padding: '10px', fontWeight: 700, fontSize: '14px',
                      cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? .7 : 1,
                    }}
                  >
                    {saving ? 'Sending…' : `Submit ${type === 'bug' ? 'Bug Report' : 'Feature Request'}`}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
